import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Mail, Plus, ArrowUpRight } from 'lucide-react';
import { api, unwrap } from '@/lib/api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

interface FormItem {
  id: number | string;
  key: string;
  title: string;
  description: string | null;
  fields: Array<{ name: string; type: string; required?: boolean }> | null;
  submissions_count?: number;
  updated_at: string;
}

interface Paginated<T> {
  data: T[];
  total: number;
}

export const Route = createFileRoute('/_app/forms/')({
  component: FormsListPage,
});

function FormsListPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['forms'],
    queryFn: () => unwrap<Paginated<FormItem>>(api.get('/forms?per_page=50')),
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Formularios</h1>
          <p className="text-sm text-neutral-500">
            Configura formularios y revisa los envíos de tus visitantes.
          </p>
        </div>
        <Button asChild>
          <Link to="/forms/new">
            <Plus className="size-4" /> Nuevo formulario
          </Link>
        </Button>
      </header>

      {isLoading && <p className="text-sm text-neutral-500">Cargando…</p>}

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {(data?.data ?? []).map((f) => (
          <Link
            key={f.id}
            to="/forms/$id"
            params={{ id: String(f.id) }}
            className="group block"
          >
            <Card className="h-full p-4 transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between">
                <span className="grid size-10 place-items-center rounded-lg bg-emerald-100">
                  <Mail className="size-5 text-emerald-700" />
                </span>
                <ArrowUpRight className="size-4 text-neutral-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </div>
              <div className="mt-3">
                <h3 className="font-semibold">{f.title}</h3>
                <p className="text-xs text-neutral-500">/{f.key}</p>
                {f.description && (
                  <p className="mt-1 line-clamp-2 text-xs text-neutral-600">{f.description}</p>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Badge variant="secondary">{(f.fields ?? []).length} campos</Badge>
                {f.submissions_count != null && (
                  <Badge variant="success">{f.submissions_count} envíos</Badge>
                )}
              </div>
            </Card>
          </Link>
        ))}
        {(!data || (data.data ?? []).length === 0) && !isLoading && (
          <Card className="col-span-full p-8 text-center text-sm text-neutral-500">
            No hay formularios configurados aún.
          </Card>
        )}
      </div>
    </div>
  );
}
